import { DeepKeyOf } from '@/components/_dashboard/promotion/formik/type';
import LoadingComponent from '@/components/LoadingComponent';
import SearchNotFound from '@/components/SearchNotFound';
import { filterListByKeys, sortList } from '@/utils/functions';

import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography } from '@mui/material';
import _ from 'lodash';
import { CSSProperties, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { ICommonTableHead, ICommonTableHeadColumn, SortType } from './commonTableHead/commonTableHead';
import { ICommonTableToolbar, ISearchBox } from './commonTableToolbar/commonTableToolbar';

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

export type RowBodyProps<T, P> = P & { data: T };

interface SortableRowProps<T, P> {
    id: string;
    data: T;
    RowBodyComponent: React.ComponentType<P & { data: T }>;
    rowBodyProps: P;
}

const SortableRow = <T, P>({ id, data, RowBodyComponent, rowBodyProps }: SortableRowProps<T, P>): JSX.Element => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        scale: isDragging ? 1.1 : 1,
        zIndex: isDragging ? 1000 : 'auto',
        boxShadow: isDragging ? '0 5px 15px rgba(0,0,0,0.3)' : 'none'
    };
    const newRowBodyProps = { ...rowBodyProps, setNodeRef, style, attributes, listeners };
    return <RowBodyComponent data={data} {...newRowBodyProps} />;
};

interface ICommonTableProps<T, P = Record<string, unknown>> {
    columnList: ICommonTableHeadColumn<T>[];
    dataList: T[];
    isLoading: boolean;
    RowBodyComponent: React.ComponentType<P & { data: T }>;
    rowBodyProps?: Omit<P, 'data'>;
    tableRowKey: DeepKeyOf<T>;
    searchBox?: ISearchBox<T>;
    tableTitle?: string;
    sx?: CSSProperties;
    backgroundColor?: string;
    filterBoxes?: JSX.Element[];
    rowsPerPageOptions?: number[];
    sortable?: {
        onSortStart?: () => Promise<void>;
        onSortEnd?: (oldIndex: number, newIndex: number, sortedData: T[]) => Promise<void>;
    };
    customerHeader?: JSX.Element;
}

export const CommonTable = <T, P = Record<string, unknown>>(props: ICommonTableProps<T, P>): JSX.Element => {
    const {
        columnList,
        dataList,
        isLoading,
        RowBodyComponent,
        rowBodyProps = {} as P,
        searchBox,
        tableRowKey,
        tableTitle,
        sx = {},
        backgroundColor,
        filterBoxes,
        rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
        sortable,
        customerHeader
    } = props;

    const [sortedDataList, setSortedDataList] = useState<T[]>(dataList);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [page, setPage] = useState(0);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const getTableRowKeyValue = (data: T, property: DeepKeyOf<T>): string => {
        const key = _.get(data, property, v4());
        if (typeof key === 'string') return key;
        if (typeof key === 'number') return String(key);
        return v4();
    };

    const handleColumnTitleOnClick = (property: DeepKeyOf<T>, sortType: SortType): void => {
        if (!property) return;
        const sortedList = sortList(dataList, sortType, property);
        setSortedDataList(sortedList);
    };

    const handleSearchValueOnChange = (value: string): void => {
        if (!searchBox) return;
        if (searchBox.list.length === 0) return;
        if (value === '') {
            setSortedDataList(dataList);
            return;
        }
        const filteredData = filterListByKeys(dataList, searchBox.list, value);
        setSortedDataList(filteredData);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDragStart = (): void => {
        if (sortable?.onSortStart) {
            sortable.onSortStart();
        }
    };

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const currentPageData = sortedDataList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        const oldIndex = currentPageData.findIndex((item) => getTableRowKeyValue(item, tableRowKey) === active.id);
        const newIndex = currentPageData.findIndex((item) => getTableRowKeyValue(item, tableRowKey) === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const newCurrentPageData = arrayMove(currentPageData, oldIndex, newIndex);

            const newSortedDataList = [...sortedDataList];
            const startIndex = page * rowsPerPage;
            newCurrentPageData.forEach((item, index) => {
                newSortedDataList[startIndex + index] = item;
            });

            setSortedDataList(newSortedDataList);

            if (sortable?.onSortEnd) {
                const globalOldIndex = startIndex + oldIndex;
                const globalNewIndex = startIndex + newIndex;
                sortable.onSortEnd(globalOldIndex, globalNewIndex, newSortedDataList);
            }
        }
    };

    useEffect(() => {
        setSortedDataList(dataList);
        setPage(0); // Reset to first page when data changes
    }, [dataList]);

    // Clamp page when sortedDataList shrinks (e.g., after search/sort)
    useEffect(() => {
        if (sortedDataList.length === 0) {
            setPage(0);
            return;
        }
        const maxPage = Math.max(0, Math.ceil(sortedDataList.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [sortedDataList.length, rowsPerPage, page]);

    const currentPageData = sortedDataList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const sortableIds = currentPageData.map((data) => getTableRowKeyValue(data, tableRowKey));

    const renderTableBody = (): JSX.Element | JSX.Element[] => {
        if (isLoading) {
            return (
                <TableRow>
                    <TableCell colSpan={8}>
                        <LoadingComponent />
                    </TableCell>
                </TableRow>
            );
        }

        if (sortedDataList.length === 0) {
            return (
                <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound isNoItem={true} />
                    </TableCell>
                </TableRow>
            );
        }
        if (sortable && sortable.onSortEnd) {
            return (
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    {currentPageData.map((data) => (
                        <SortableRow
                            key={getTableRowKeyValue(data, tableRowKey)}
                            id={getTableRowKeyValue(data, tableRowKey)}
                            data={data}
                            RowBodyComponent={RowBodyComponent}
                            rowBodyProps={rowBodyProps as P}
                        />
                    ))}
                </SortableContext>
            );
        }
        return currentPageData.map((data) => (
            <RowBodyComponent key={getTableRowKeyValue(data, tableRowKey)} data={data} {...(rowBodyProps as P)} />
        ));
    };

    const tableContent = (
        <>
            {customerHeader ? (
                customerHeader
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        margin: 2,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    {tableTitle && (
                        <Typography sx={{ marginY: 'auto' }} variant="h4">
                            {tableTitle}
                        </Typography>
                    )}
                    {searchBox && (
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <ICommonTableToolbar
                                placeholder={searchBox.placeholder}
                                handleSearchValueOnChange={handleSearchValueOnChange}
                                extraAction={searchBox.extraAction}
                            />
                        </Box>
                    )}
                    {filterBoxes && filterBoxes.map((filterBox, index) => <Box key={index}>{filterBox}</Box>)}
                </Box>
            )}
            <TableContainer sx={{ minWidth: 800, ...sx }}>
                <Table stickyHeader aria-label="sticky table">
                    <ICommonTableHead
                        columnList={columnList}
                        handleColumnTitleOnClick={handleColumnTitleOnClick}
                        backgroundColor={backgroundColor}
                    />
                    <TableBody>{renderTableBody()}</TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={sortedDataList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </>
    );

    if (sortable && sortable.onSortEnd) {
        return (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                {tableContent}
            </DndContext>
        );
    }

    return tableContent;
};
